// src/pages/invoices/components/CompanyDropdown.tsx
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getStatesLabel } from '@/lib/constants/common';
import { getIndustryLabel } from '@/lib/constants/company';
import { cn } from '@/lib/utils';
import { Company } from '@/types/company.types';
import { Building2, Check, ChevronsUpDown, Plus } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

interface CompanyDropdownProps {
  companies: Company[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const CompanyDropdown = ({
  companies,
  value,
  onChange,
  placeholder = 'Select company...',
}: CompanyDropdownProps) => {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const navigate = useNavigate();

  const selectedCompany = companies.find((c) => c._id === value);

  const filteredCompanies = companies.filter((company) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const stateLabel = getStatesLabel(company?.placeOfSupply || '')?.toLowerCase() || '';
    const industryLabel = getIndustryLabel(company?.companyIndustry || '')?.toLowerCase() || '';

    return (
      company.companyName?.toLowerCase().includes(searchLower) ||
      company.companyAddress?.city?.toLowerCase().includes(searchLower) ||
      stateLabel.includes(searchLower) ||
      industryLabel.includes(searchLower)
    );
  });

  const handleSelect = (company: Company) => {
    onChange(company._id);
    setOpen(false);
    setSearch('');
  };

  const handleAddNew = () => {
    setOpen(false);
    navigate('/companies/new');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        type="button"
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm',
          'ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
          'hover:bg-accent hover:text-accent-foreground'
        )}
      >
        {selectedCompany ? (
          <span className="truncate text-left flex-1">{selectedCompany.companyName}</span>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>

      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company..."
            className="w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            autoFocus
          />
        </div>

        <div className="max-h-60 overflow-auto p-1">
          {filteredCompanies.length === 0 ? (
            <div className="py-8 text-center">
              <Building2 className="h-8 w-8 mx-auto text-muted-foreground/70 mb-2" />
              <p className="text-sm text-muted-foreground">No company found</p>
              <p className="text-xs text-muted-foreground mt-1">Try a different search term</p>
            </div>
          ) : (
            filteredCompanies.map((company) => (
              <div
                key={company._id}
                onClick={() => handleSelect(company)}
                className={cn(
                  'flex cursor-pointer items-center rounded-sm px-2 py-2 text-sm hover:bg-accent hover:text-accent-foreground',
                  value === company._id && 'bg-accent'
                )}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value === company._id ? 'opacity-100' : 'opacity-0'
                  )}
                />
                <div className="flex flex-col min-w-0 flex-1 gap-1">
                  {/* Company Name + Industry Badge */}
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{company.companyName}</span>
                    {company.companyIndustry && (
                      <Badge className="shrink-0 text-[11px] px-1.5 py-0 h-[18px] bg-primary/50">
                        {getIndustryLabel(company.companyIndustry)}
                      </Badge>
                    )}
                  </div>

                  {/* City & Place of Supply */}
                  <span className="text-xs text-muted-foreground">
                    {[company.companyAddress?.city, getStatesLabel(company?.placeOfSupply || '')]
                      .filter(Boolean)
                      .join('\u00A0\u00A0•\u00A0\u00A0')}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="border-t p-1">
          <div
            onClick={handleAddNew}
            className="flex cursor-pointer items-center rounded-sm px-2 py-2 text-sm text-primary hover:bg-accent"
          >
            <Plus className="mr-2 h-4 w-4" />
            Add New Company
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CompanyDropdown;
